<template>
  <div class="attendance-wrapper" ref="sectionTriggerRef">
    <!-- ELEGANT MODAL: PINK BACKGROUND AND WHITE LETTERS -->
    <transition name="modal-fade">
      <div v-if="showWelcomeModal" class="modal-overlay" @click.self="closeModal">
        <div class="modal-card">
          <div class="modal-header-decor">
            <span class="line-decor"></span>
            <span class="dot-decor"></span>
            <span class="line-decor"></span>
          </div>
          <h3 class="modal-title">We Hope to See You!</h3>
          <p class="modal-text">
            Please confirm your attendance to be part of this great day and share unforgettable moments together.
          </p>
          <button class="modal-btn" @click="closeModal">
            Confirm RSVP
            <span class="btn-sheen" aria-hidden="true"></span>
          </button>
        </div>
      </div>
    </transition>

    <!-- MAIN COMPONENT -->
    <div 
      class="confirmation-container d-flex align-items-center justify-content-center"
      :class="{ 'compact-view': asistenciaConfirmada || showQRCode }"
    >
      <div 
        class="confirmation-card p-4 p-md-5 rounded-4 shadow-lg"
        :class="{ 'compact-card': asistenciaConfirmada || showQRCode }"
        ref="formCardRef"
      >
        <!-- Confirmation View -->
        <div v-if="asistenciaConfirmada || showQRCode" class="qr-view text-center">
          <p class="qr-header">Thank you for confirming your attendance</p>
        </div>

        <!-- Step-by-Step Form -->
        <div v-else>
          <div class="form-header">
            <h2 ref="tituloAnimado" class="form-title">
              Confirm<br><span class="subtitulo">Attendance</span>
            </h2>
            <br>

            <div class="progress-container">
              <div
                class="progress-bar"
                :style="{ width: ((currentStep - 1) / (totalSteps - 1)) * 100 + '%' }"
              ></div>
            </div>
            <p class="form-subtitle">Step {{ currentStep }} of {{ totalSteps }}</p>
          </div>

          <form @submit.prevent="handleSubmit" class="confirmation-form">
            <!-- Step 1: Name and Phone -->
            <div v-if="currentStep === 1" class="step-box">
              <div class="form-group">
                <label for="name" class="form-label">Name</label>
                <input
                  id="name"
                  v-model.trim="name"
                  type="text"
                  class="form-control"
                  placeholder="Enter your full name"
                  required
                >
              </div>
              <div class="form-group">
                <label for="phone" class="form-label">Phone</label>
                <input
                  id="phone"
                  v-model.trim="phone"
                  type="tel"
                  class="form-control"
                  placeholder="Enter your phone number"
                  required
                  pattern="[0-9]{10}"
                  title="Enter a 10-digit phone number"
                >
              </div>
            </div>

            <!-- Step 2: Invited by (hidden in XV or YM) -->
            <div v-if="currentStep === 2 && !isXvEvent" class="step-box">
              <div class="form-group">
                <label class="form-label">Invited by?</label>
                <div class="radio-group">
                  <div class="radio-option">
                    <input id="novia" v-model="invitedBy" type="radio" value="novia" class="radio-input" required>
                    <label for="novia" class="radio-label">Bride</label>
                  </div>
                  <div class="radio-option">
                    <input id="novio" v-model="invitedBy" type="radio" value="novio" class="radio-input">
                    <label for="novio" class="radio-label">Groom</label>
                  </div>
                </div>
              </div>
            </div>

            <!-- Step 3: Attendance -->
            <div v-if="currentStep === asistenciaStep" class="step-box">
              <div class="form-group">
                <label class="form-label">Will you attend?</label>
                <div class="radio-group">
                  <div class="radio-option">
                    <input id="si" v-model="attending" type="radio" value="si" class="radio-input" required>
                    <label for="si" class="radio-label">Yes</label>
                  </div>
                  <div class="radio-option">
                    <input id="no" v-model="attending" type="radio" value="no" class="radio-input">
                    <label for="no" class="radio-label">No</label>
                  </div>
                </div>
              </div>
            </div>

            <!-- Step 4: Number of guests -->
            <div v-if="currentStep === totalSteps && attending !== 'no'" class="step-box">
              <div class="form-group">
                <label for="guests" class="form-label">How many are attending?</label>
                <select
                  id="guests"
                  v-model.number="guests"
                  class="form-select"
                  :disabled="attending === 'no' || loading"
                  required
                >
                  <option :value="null" disabled>Select an option</option>
                  <option v-for="n in guestsOptions" :key="n" :value="n">{{ n }}</option>
                </select>
                <div v-if="loading" class="loading-indicator">Loading options...</div>
              </div>
            </div>

            <!-- Navigation Buttons -->
            <div class="d-flex justify-content-between mt-3">
              <button type="button" class="submit-button" @click="prevStep" v-if="currentStep > 1">Back</button>
              <button v-if="currentStep < totalSteps" type="button" class="submit-button" @click="nextStep">Next</button>
              <button v-if="currentStep === totalSteps" type="submit" class="submit-button" :disabled="isSubmitting">
                <span v-if="!isSubmitting">Confirm</span>
                <span v-else class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
              </button>
            </div>

            <div v-if="errorMessage" class="alert alert-danger mt-3">{{ errorMessage }}</div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, watch, computed, nextTick } from 'vue';
import axios from 'axios';
import baseUrl from '@/api';
import gsap from 'gsap';
import Swal from 'sweetalert2';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

export default {
  name: 'AttendanceConfirmation',
  props: {
    idInvitado: { type: Number, required: true, validator: v => Number.isInteger(v) && v > 0 },
    nombre: { type: String, default: '' },
    idFestejado: { type: Number, default: null }
  },
  setup(props) {
    const name = ref('');
    const phone = ref('');
    const invitedBy = ref('');
    const attending = ref('');              // 'si' | 'no'
    const guests = ref(null);                // numeric or null
    const guestsOptions = ref([1, 2, 3, 4, 5]);
    const loading = ref(true);
    const isSubmitting = ref(false);
    const errorMessage = ref('');

    const tituloAnimado = ref(null);
    const sectionTriggerRef = ref(null);
    const showWelcomeModal = ref(false);

    const qrCode = ref(null);
    const showQRCode = ref(false);
    const asistenciaConfirmada = ref(false);
    const currentStep = ref(1);
    const formCardRef = ref(null);

    const closeModal = () => {
      showWelcomeModal.value = false;
    };

    const isXvEvent = computed(() => {
      const eventName = props.nombre?.toLowerCase();
      return ['xv', 'ym'].includes(eventName);
    });

    const totalSteps = computed(() => isXvEvent.value ? 3 : 4);
    const asistenciaStep = computed(() => isXvEvent.value ? 2 : 3);

    const animateStep = () => {
      nextTick(() => {
        const box = formCardRef.value?.querySelector('.step-box');
        if (!box) return;
        gsap.fromTo(
          box,
          { y: 50, opacity: 0, transformPerspective: 300, rotationX: -10 },
          { y: 0, opacity: 1, duration: 0.6, ease: 'power2.out', rotationX: 0 }
        );
      });
    };

    const nextStep = async () => {
      if (currentStep.value === 1) {
        if (!name.value.trim() || !phone.value.trim()) {
          errorMessage.value = 'Please complete your name and phone number.';
          return;
        }
      }

      if (currentStep.value === 2 && !isXvEvent.value) {
        if (!invitedBy.value) {
          errorMessage.value = 'Please select who invited you.';
          return;
        }
      }

      if (currentStep.value === asistenciaStep.value) {
        if (!attending.value) {
          errorMessage.value = 'Please select whether you will attend or not.';
          return;
        }
        if (attending.value === 'no') {
          currentStep.value = totalSteps.value;
          await nextTick();
          handleSubmit();
          return;
        }
      }

      if (currentStep.value === totalSteps.value && attending.value === 'si') {
        if (guests.value == null || Number.isNaN(Number(guests.value))) {
          errorMessage.value = 'Please select how many people will attend.';
          return;
        }
      }

      errorMessage.value = '';
      if (currentStep.value < totalSteps.value) {
        currentStep.value++;
        animateStep();
      }
    };

    const prevStep = () => {
      if (currentStep.value > 1) {
        currentStep.value--;
        animateStep();
      }
    };

    const fetchGuestLimit = async () => {
      try {
        const response = await axios.get(`${baseUrl}/${props.nombre ? `${props.nombre}/` : ''}${props.idFestejado ? `${props.idFestejado}/` : ''}${props.idInvitado}`);
        const numPases = Number(response.data.num_pase);
        guestsOptions.value = numPases > 0
          ? Array.from({ length: numPases }, (_, i) => i + 1)
          : [1, 2];
      } catch (error) {
        console.error('Error fetching guest limit:', error);
      } finally {
        loading.value = false;
      }
    };

    const handleSubmit = async () => {
      if (attending.value === 'no') guests.value = 0;

      isSubmitting.value = true;
      errorMessage.value = '';

      try {
        const formData = {
          nombre_asistencia: name.value.trim(),
          telefono_invitado: phone.value.trim(),
          invitedby: isXvEvent.value ? 'novia' : invitedBy.value,
          asistira: attending.value,                       // 'si' | 'no'
          id_festejada: props.idFestejado,
          id_invitado: props.idInvitado,
          num_pases: Number(attending.value === 'no' ? 0 : (guests.value ?? 0)),
        };

        await axios.post(`${baseUrl}/asistencia`, formData);

        if (attending.value === 'no') {
          Swal.fire({
            icon: 'info',
            title: 'Thank you for letting us know!',
            text: 'We are very sorry you cannot attend.',
            confirmButtonColor: '#d88cbd',
          });
          asistenciaConfirmada.value = true;
        } else {
          showQRCode.value = true;
        }
      } catch (error) {
        console.error('Error confirming attendance:', error);
        errorMessage.value = 'There was an error confirming. Please try again.';
      } finally {
        isSubmitting.value = false;
      }
    };

    watch(attending, (newValue) => {
      if (newValue === 'no') {
        guests.value = 0;
      } else if (newValue === 'si' && (guests.value === 0 || guests.value == null)) {
        guests.value = 1;
      }
    });

    onMounted(() => {
      fetchGuestLimit();
      animateStep();

      nextTick(() => {
        setTimeout(() => {
          ScrollTrigger.refresh();

          if ('IntersectionObserver' in window && sectionTriggerRef.value) {
            const observer = new IntersectionObserver((entries) => {
              entries.forEach(entry => {
                if (entry.isIntersecting) {
                  showWelcomeModal.value = true;
                  observer.disconnect();
                }
              });
            }, { threshold: 0.3 });

            observer.observe(sectionTriggerRef.value);
          } else {
            ScrollTrigger.create({
              trigger: sectionTriggerRef.value,
              start: 'top 80%',
              once: true,
              refreshPriority: 1,
              onEnter: () => {
                showWelcomeModal.value = true;
              }
            });
          }

          gsap.fromTo(
            tituloAnimado.value,
            { y: -150, opacity: 0, scale: 1.2 },
            {
              y: 0,
              opacity: 1,
              scale: 1,
              duration: 1.2,
              ease: 'bounce.out',
              scrollTrigger: {
                trigger: tituloAnimado.value,
                start: 'top 80%',
                toggleActions: 'restart none none reset'
              }
            }
          );
        }, 100);
      });
    });

    return {
      name, phone, invitedBy, attending, guests, guestsOptions,
      loading, isSubmitting, errorMessage, isXvEvent, asistenciaConfirmada,
      qrCode, showQRCode, currentStep, nextStep, prevStep, handleSubmit,
      totalSteps, asistenciaStep, formCardRef, tituloAnimado,
      sectionTriggerRef, showWelcomeModal, closeModal
    };
  }
};
</script>

<style scoped>
/* Estilos base */
.attendance-wrapper {
  position: relative;
  width: 100%;
}

/* ==================== MODAL LUJO: FONDO ROSA Y TEXTO BLANCO ==================== */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(20, 18, 22, 0.45);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 16px;
  box-sizing: border-box;
}

.modal-card {
  background: #150954; /* Fondo en tonos rosas elegantes */
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 24px;
  padding: 36px 24px;
  width: 100%;
  max-width: min(340px, 85vw);
  text-align: center;
  box-shadow: 0 25px 45px rgba(0, 0, 0, 0.15), 0 0 0 6px rgba(223, 145, 166, 0.2);
  position: relative;
  overflow: hidden;
  box-sizing: border-box;
}

/* Decoración superior fina */
.modal-header-decor {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  margin-bottom: 16px;
}

.line-decor {
  width: 30px;
  height: 1px;
  background: rgba(255, 255, 255, 0.6);
}

.dot-decor {
  width: 4px;
  height: 4px;
  background: #ffffff;
  border-radius: 50%;
}

.modal-title {
  font-family: 'Playfair Display', serif;
  font-weight: 500;
  font-size: 1.8rem;
  color: #ffffff; /* Letras blancas */
  letter-spacing: 0.5px;
  margin-bottom: 12px;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.15);
}

.modal-text {
  font-family: 'Cormorant Garamond', 'Karla', serif;
  font-size: 1.05rem;
  color: rgba(255, 255, 255, 0.95); /* Letras blancas suaves */
  line-height: 1.5;
  margin-bottom: 24px;
}

.modal-btn {
  position: relative;
  background: #ffffff;
  color: #043b73; /* Texto rosa elegante en el botón blanco */
  font-family: 'Cinzel', 'Karla', sans-serif;
  font-weight: 600;
  font-size: 0.85rem;
  letter-spacing: 1.2px;
  text-transform: uppercase;
  padding: 12px 22px;
  border: none;
  border-radius: 50px;
  cursor: pointer;
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.1);
  transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
  overflow: hidden;
  width: 100%;
}

.modal-btn:hover {
  background: #f9f0f3;
  transform: translateY(-2px);
  box-shadow: 0 10px 22px rgba(0, 0, 0, 0.15);
}

.btn-sheen {
  position: absolute;
  top: -30%;
  left: -30%;
  width: 50%;
  height: 160%;
  transform: rotate(20deg);
  background: url('@/assets/dorado.jpg') center center / cover no-repeat;
  color: #1d0f5d;
  pointer-events: none;
  opacity: 0;
  transition: opacity 0.3s;
}

.modal-btn:hover .btn-sheen {
  opacity: 1;
  animation: sheenEffect 0.8s ease;
}

@keyframes sheenEffect {
  0% { transform: translateX(-100%) rotate(20deg); }
  100% { transform: translateX(300%) rotate(20deg); }
}

/* Transiciones suaves para el modal */
.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.4s ease;
}
.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}
.modal-fade-enter-active .modal-card {
  animation: modalScaleUp 0.4s cubic-bezier(0.16, 1, 0.3, 1) forwards;
}
.modal-fade-leave-active .modal-card {
  animation: modalScaleDown 0.3s ease forwards;
}

@keyframes modalScaleUp {
  from { opacity: 0; transform: scale(0.9) translateY(15px); }
  to { opacity: 1; transform: scale(1) translateY(0); }
}

@keyframes modalScaleDown {
  from { opacity: 1; transform: scale(1) translateY(0); }
  to { opacity: 0; transform: scale(0.9) translateY(15px); }
}

/* Resto de contenedores y estilos previos */
.confirmation-container {
  min-height: 70vh;
  padding: 2rem 1rem;
  transition: all 0.3s ease;
}

.confirmation-card {
  width: 100%;
  max-width: 500px;
  background: white;
  background: linear-gradient(to bottom, #ffffff, #f9f9f9);
  border: 1px solid #e0e0e0;
  transition: all 0.3s ease;
}

.compact-view {
  min-height: 70vh;
  align-items: flex-start;
  padding-top: 4rem;
}

.compact-card {
  max-width: 400px;
  padding: 2rem;
}

.form-header {
  margin-bottom: 2rem;
  text-align: center;
}

.form-title {
  font-family: 'british', cursive;
  font-size: 2rem;
  color: #043b73;
  margin-bottom: 0.5rem;
}

.form-subtitle {
  font-family: 'Karla', sans-serif;
  color: #6c757d;
  font-size: 1rem;
}

.form-group {
  margin-bottom: 1.5rem;
}

.form-label {
  display: block;
  margin-bottom: 0.5rem;
  font-family: 'Karla', sans-serif;
  font-weight: 500;
  color: #495057;
}

.form-control,
.form-select {
  width: 100%;
  padding: 0.75rem 1rem;
  font-size: 1rem;
  border: 1px solid #ced4da;
  border-radius: 0.5rem;
  background-color: rgba(255, 255, 255, 0.8);
  transition: border-color 0.3s, box-shadow 0.3s;
}

.form-control:focus,
.form-select:focus {
  border-color: #043b73;
  box-shadow: 0 0 0 0.25rem rgba(213, 146, 176, 0.25);
  outline: none;
}

.radio-group {
  display: flex;
  gap: 1.5rem;
}

.radio-option {
  display: flex;
  align-items: center;
}

.radio-input {
  margin-right: 0.5rem;
  accent-color: #043b73;
}

.radio-label {
  font-family: 'Karla', sans-serif;
  color: #495057;
}

.submit-button {
  width: 100%;
  padding: 0.75rem;
  font-family: 'FeelingPassionate', cursive;
  font-size: 1.25rem;
  color: rgb(249, 249, 249);
  background-color: #150954;
  border: none;
  border-radius: 0.5rem;
  cursor: pointer;
  transition: background-color 0.3s;
}

.submit-button:hover {
  background-color: #c07d9b;
}

.submit-button:disabled {
  background-color: #d592b0;
  opacity: 0.7;
  cursor: not-allowed;
}

.qr-view {
  padding: 1rem;
}

.qr-header {
  font-family: "Playfair Display", serif;
  font-size: 18px;
  color: #1d0f5d;
  margin-bottom: 1.5rem;
}

.qr-wrapper {
  margin: 1rem 0;
}

.qr-code {
  width: 180px;
  height: 180px;
  border: 8px solid white;
  border-radius: 0.5rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.qr-instructions {
  font-family: 'Karla', sans-serif;
  color: #6c757d;
  font-size: 1rem;
  margin-top: 1rem;
}

.alert {
  padding: 0.75rem 1rem;
  border-radius: 0.5rem;
}

.qr-error-message {
  color: #dc3545;
  font-weight: 500;
}

.loading-indicator {
  font-size: 0.875rem;
  color: #6c757d;
  margin-top: 0.5rem;
}

@media (max-width: 576px) {
  .confirmation-card {
    padding: 1.5rem;
  }
  .form-title {
    font-size: 1.75rem;
  }
  .radio-group {
    gap: 1rem;
  }
}

.progress-container {
  height: 8px;
  margin-top: 40px;
  background-color: #e0e0e0;
  border-radius: 4px;
  margin-bottom: 1rem;
  overflow: hidden;
}

.progress-bar {
  height: 100%;
  background-color: #83808b;
  transition: width 0.4s ease;
}
</style>